/**
 * Фильтры, поиск, сортировка и страница списка дел — в адресе страницы (доработка шага 11): обновление страницы,
 * «назад» и «вперёд» и ссылка, открытая в новой вкладке, возвращают тот же список. Один хук на все три списка:
 * страница «Дела» и блоки «Мои дела» на светофоре и рабочем столе (их параметры не пересекаются с периодом
 * `days`/`from`/`to`, который пишет `usePeriodState`).
 *
 * История: каждое изменение фильтра, сортировки и страницы — новая запись, поэтому «назад» возвращает прежнюю выборку.
 * Поиск: начало и сброс запроса — новая запись, правка уже набранного запроса заменяет текущую (иначе «назад» шёл бы
 * по буквам; `CaseFilters` и так отдаёт ввод после паузы).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ChipKey, FilterState } from './CaseFilters';
import { DEFAULT_FILTERS } from './CaseFilters';
import type { CaseSort } from './CasesTable';
import { isCaseSort } from './CasesTable';

const FILTER_KEYS: readonly (keyof FilterState)[] = ['chip', 'q', 'stage', 'category', 'priority', 'executor', 'department', 'source', 'deadline'];

function readChip(raw: string | null, chips: readonly ChipKey[]): ChipKey {
    if (raw && (chips as readonly string[]).includes(raw)) return raw as ChipKey;
    return DEFAULT_FILTERS.chip;
}

function readNumber(raw: string | null, fallback: number): number {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
}

export interface CaseListParams {
    filters: FilterState;
    page: number;
    limit: number;
    sort: CaseSort;
    /** Правка фильтров и поиска; список возвращается на первую страницу. */
    changeFilters: (patch: Partial<FilterState>) => void;
    /** Страница, размер страницы, сортировка; всё, кроме страницы, возвращает на первую. */
    changeParam: (key: 'page' | 'limit' | 'sort', value: string | number | null) => void;
}

export function useCaseListParams(chips: readonly ChipKey[]): CaseListParams {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters: FilterState = useMemo(() => {
        const read = (key: keyof FilterState) => searchParams.get(key) ?? '';
        return {
            chip: readChip(searchParams.get('chip'), chips),
            q: read('q'),
            stage: read('stage'),
            category: read('category'),
            priority: read('priority'),
            executor: read('executor'),
            department: read('department'),
            source: read('source'),
            deadline: read('deadline'),
        };
    }, [searchParams, chips]);
    const page = readNumber(searchParams.get('page'), 1);
    const limit = readNumber(searchParams.get('limit'), 25);
    const sortRaw = searchParams.get('sort');
    const sort: CaseSort = isCaseSort(sortRaw) ? sortRaw : 'deadline';

    const update = useCallback(
        (patch: Record<string, string | number | null | undefined>, keepPage: boolean) => {
            const before = searchParams.get('q') ?? '';
            const typing = Object.keys(patch).length === 1 && 'q' in patch && before !== '' && Boolean(patch.q);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    for (const [key, value] of Object.entries(patch)) {
                        // Значение по умолчанию в адрес не пишется: ссылка без фильтров остаётся короткой.
                        const isDefault = value === null || value === undefined || value === '' || (key === 'chip' && value === DEFAULT_FILTERS.chip) || (key === 'sort' && value === 'deadline') || (key === 'page' && Number(value) === 1);
                        if (isDefault) next.delete(key);
                        else next.set(key, String(value));
                    }
                    if (!keepPage) next.delete('page');
                    return next;
                },
                { replace: typing },
            );
        },
        [searchParams, setSearchParams],
    );

    const changeFilters = useCallback(
        (patch: Partial<FilterState>) => {
            const clean: Record<string, string> = {};
            for (const key of FILTER_KEYS) if (key in patch) clean[key] = patch[key] ?? '';
            update(clean, false);
        },
        [update],
    );
    const changeParam = useCallback((key: 'page' | 'limit' | 'sort', value: string | number | null) => update({ [key]: value }, key === 'page'), [update]);

    return { filters, page, limit, sort, changeFilters, changeParam };
}
